import type { ObjectSchema } from 'yup';
import * as yup from 'yup';

import type {
  GroupResultDTO,
  UserResultDTO,
} from '@nora-monorepo/nora-api-client';

type Translate = (key: string, params?: unknown) => string;

interface ConversationMemberWithRoleSchemaValue {
  role: string;
}

interface ConversationUserWithRoleSchemaValue extends ConversationMemberWithRoleSchemaValue {
  user: UserResultDTO;
}

interface ConversationGroupWithRoleSchemaValue extends ConversationMemberWithRoleSchemaValue {
  group: GroupResultDTO;
}

interface ConversationMemberListSchemaValue {
  users: ConversationUserWithRoleSchemaValue[];
  groups: ConversationGroupWithRoleSchemaValue[];
}

/**
 * Creates the yup schema for validating conversation members against the
 * currently loaded conversation role metadata.
 */
export function createConversationMemberListSchema(
  t: Translate,
  adminRoleIds: Set<string>,
  allowedRoleIds: Set<string>,
): ObjectSchema<ConversationMemberListSchemaValue> {
  const adminRoleRequiredMessage = t('conversation.validation.adminRoleRequired');
  const invalidRoleSelectedMessage = t('conversation.validation.invalidRoleSelected');

  const isAdmin = (member?: ConversationMemberWithRoleSchemaValue): boolean => {
    if (!member || adminRoleIds.size === 0) {
      return true;
    }

    return adminRoleIds.has(member.role);
  };

  const hasValidRole = (member?: ConversationMemberWithRoleSchemaValue): boolean => {
    if (!member || allowedRoleIds.size === 0) {
      return true;
    }

    return allowedRoleIds.has(member.role);
  };

  return yup.object({
    users: yup.array().of(
      yup.object({
        user: yup.mixed<UserResultDTO>().required(),
        role: yup.string().required(),
      }),
    ).required().default([]),
    groups: yup.array().of(
      yup.object({
        group: yup.mixed<GroupResultDTO>().required(),
        role: yup.string().required(),
      }),
    ).required().default([]),
  })
    .required()
    .test(
      'has-admin',
      adminRoleRequiredMessage,
      value => (value?.users.some(isAdmin) ?? false) || (value?.groups.some(isAdmin) ?? false),
    )
    .test('valid-member-roles', invalidRoleSelectedMessage, (value) => {
      if (!value) {
        return false;
      }

      const usersHaveValidRoles = value.users.every(hasValidRole);
      const groupsHaveValidRoles = value.groups.every(hasValidRole);

      return usersHaveValidRoles && groupsHaveValidRoles;
    });
}
